// Lifted from useMatrixAnswersData; pure so basic-output can reuse without React/Jotai.
//
// Design: "fat input, dumb function". The wrapping hook resolves Jotai context
// and sibling hooks (mapQuestionsDiff, totalData) and passes the already-resolved
// values here, so the offline batch path and the interactive page share one
// derivation and cannot drift.

import * as R from 'remeda'

import { type Question } from '~/hooks/useSurveyQuery'
import {
  exists,
  findByPath,
  type GroupAndAll,
  mapItemsToObj,
  pathOr,
} from '~/utils'

import { type CountAnswers } from './atomTypes'
import {
  type AnswerOption,
  type QuestionsDiff,
  type QuestionsDiffColumnKey,
  type RowData,
} from './common'

export type MatrixAnswerData = RowData<string> & {
  answerOption: AnswerOption
}

export type MatrixAnswersDataInput = {
  group: GroupAndAll
  mapQuestionsDiff: <T>(
    mapFunc: (questionsDiff: QuestionsDiff, index: number) => T,
  ) => Record<QuestionsDiffColumnKey, T>
  questionId: Question['question_id']
  questions: Question['question_id'][]
  results: (CountAnswers[] | null)[]
  totalCount: number
}

export const buildMatrixAnswersData = ({
  group,
  mapQuestionsDiff,
  questionId,
  questions,
  results,
  totalCount,
}: MatrixAnswersDataInput): MatrixAnswerData[] =>
  R.pipe(
    R.zip(questions, results),
    (tuples) => tuples.find(([id]) => id === questionId),
    (tuple) => tuple?.[1]?.filter(exists) ?? [],
    (countAnswersArray) =>
      (countAnswersArray[0]?.answers ?? []).map((answer) => {
        const counts = mapItemsToObj(
          countAnswersArray,
          (countAnswers) =>
            R.pipe(
              group === 'all'
                ? countAnswers
                : findByPath(countAnswers.groupAnswers ?? [], 'group', group),
              (result) => result?.answers ?? [],
              findByPath(
                'answerOption.option',
                answer.answerOption?.option ?? NaN,
              ),
              pathOr('count', NaN),
            ),
          'questionId',
        )

        const diffCounts = mapQuestionsDiff(
          ([left, right]) => counts[left] - counts[right],
        )

        return {
          answerOption: answer.answerOption as AnswerOption,
          counts: { ...counts, ...diffCounts } as Record<string, number>,
        }
      }),
    (answersData) =>
      answersData.map((answerData) => {
        const ratios = R.mapValues(
          answerData.counts,
          (count) => count / totalCount,
        )
        const diffRatios = mapQuestionsDiff(
          ([left, right]) => ratios[left] - ratios[right],
        )

        return { ...answerData, ratios: { ...ratios, ...diffRatios } }
      }),
  )
